import React, { useEffect, useState } from 'react';
import { Grid } from "antd";
import { workspaceAPI } from '../workspace.service';
import {
  KoForm,
  KoFormBuilder,
  KoLoader,
  KoUserSelect,
  KoInput,
  KoTextArea,
  KoCheckBox,
  KoSelect,
  KoRating,
  KoSlider,
  KoDate,
  KoAttachment,
  KoDataTableEdit,
  KoTitle,
  KoButton,
  KoNano
} from 'packages';
import styles from './formrenderer.module.scss';
import KoIcon from '@kwapio/icons';

const { useBreakpoint } = Grid;

export default function FormRenderer({ formId, form, onSubmit, workspaceId, initialValues }) {
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [dataSource, setDataSource] = useState([]); // starts empty
  const screens = useBreakpoint();

  const widgetMap = {
    input: KoInput,
    textarea: KoTextArea,
    checkbox: KoCheckBox,
    select: KoSelect,
    rating: KoRating,
    slider: KoSlider,
    date: KoDate,
    upload: KoAttachment,
    table: KoDataTableEdit,
    leads: KoUserSelect,
    heading: KoTitle,
    subheading: KoTitle
  };

  const displayOnlyTypes = ['heading', 'subheading', 'separator'];

  const buildRow = (columns) => {
    return columns.reduce((acc, item) => {
      acc[item.dataIndex] = "";
      return acc;
    }, { key: KoNano("row") });
  };

  const handleAdd = (columns) => {
    setDataSource(prev => [...prev, buildRow(columns)]);
  };

  const handleDelete = (key) => {
    setDataSource(prev => prev.filter(item => item.key !== key));
  };

  useEffect(() => {
    const getFormData = async() => {
      setLoading(true);
      try {
        const res = await workspaceAPI.getFormData(workspaceId, formId);

        const fields = res?.form.fields.map(field => {
          let widget;
          const dynamicProps = {};

          const colSpan = screens.xs ? 24 : (field.colSpan || 6);

          if (displayOnlyTypes.includes(field.type)) {
            widget = () => (
              <KoTitle
                level={field.type === 'heading' ? 4 : 5}
                text={field.heading || ''}
                style={{
                  color: field.color || '#000',
                  textAlign: field.align || 'left',
                  gridColumn: `span ${colSpan}`
                }}
              />
            );
          } else {
            const Widget = widgetMap[field.type] || KoInput;

            if (field.type === 'select') {
              dynamicProps.options = field.options || [];
            }

            if (field.type === 'slider') {
              if (field.min !== undefined) dynamicProps.min = field.min;
              if (field.max !== undefined) dynamicProps.max = field.max;
            }

            if (field.type === 'table') {
              const baseCols = field.options || [];

              // always show at least one empty row
              const ds = dataSource.length ? dataSource : [buildRow(baseCols)];
              setDataSource(ds);

              const actionCol = {
                title: ' ',
                dataIndex: 'operation',
                width: 40,
                fixed: 'right',
                render: (_, record, i) =>
                  ds.length >= 1 ? (
                    <div className="editTableCol">
                      <KoButton
                        icon={<KoIcon width={12} height={12} name="DeleteOutlined" />}
                        className={styles.actionBtn}
                        onClick={() => handleDelete(record.key)}
                        label=""
                      />
                      {ds?.length === i + 1 && (
                        <KoButton
                          icon={
                            <KoIcon
                              color="var(--palette-gray-7)"
                              width={17}
                              height={17}
                              name="PlusOutlined"
                            />
                          }
                          className={styles.actionBtn}
                          onClick={() => handleAdd(baseCols)}
                          label=""
                        />
                      )}
                    </div>
                  ) : null
              };

              dynamicProps.defaultColumns = [...baseCols, actionCol];
              dynamicProps.dataSource = ds;
            }

            if (field.placeholder) {
              dynamicProps.placeholder = field.placeholder;
            }

            widget = (props) => (
              <Widget
                {...props}
                {...dynamicProps}
                field={field}
                style={{ gridColumn: `span ${colSpan}` }}
              />
            );
          }

          return {
            key: field.id,
            label: displayOnlyTypes.includes(field.type) ? undefined : field.label,
            colSpan,
            widget,
            rules: field.required
              ? [{ required: true, message: `Please enter ${field.label}` }]
              : []
          };
        });

        setMeta({
          columns: res.columns || 6,
          formItemLayout: null,
          initialValues,
          colon: true,
          fields
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    getFormData();
  }, [formId, workspaceId, initialValues, screens]);

  if (loading || !meta) return <KoLoader />;

  return (
    <div className={styles.wrapper}>
      <div className={styles.formWrapper}>
        <KoForm form={form} layout="vertical" onFinish={onSubmit} onSubmit={onSubmit}>
          <KoFormBuilder form={form} meta={meta} />
        </KoForm>
      </div>
    </div>
  );
}
